'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useAuth } from './AuthProvider';
import AccountAvatar from './AccountAvatar';
import LibraryNavigation from './LibraryNavigation';
import { accountIdentity } from '@/lib/account-identity';
import { UsageIndicator } from './UsageIndicator';
import { ThemeToggle } from './ThemeToggle';

export function MobileNav() {
  const [isOpen, setIsOpen] = useState(false);
  const { user, loading, openSignIn, signOut, accountProfile } = useAuth();

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const identity = accountIdentity(user, accountProfile);

  return (
    <div className="md:hidden flex items-center gap-2">
      <Link href="/upload" onClick={() => setIsOpen(false)} className="ui-button ui-button--primary">Upload</Link>
      {/* Hamburger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="ui-button ui-button--ghost ui-button--icon -mr-1"
        aria-expanded={isOpen}
        aria-controls="mobile-navigation"
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
      >
        {isOpen ? (
          <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        )}
      </button>

      {/* Overlay + slide-down menu */}
      {isOpen && (
        <>
          <div className="fixed inset-0 top-14 bg-black/40 z-40" onClick={() => setIsOpen(false)} />
          <div id="mobile-navigation" className="fixed inset-x-0 bottom-0 top-14 z-50 overflow-y-auto overscroll-contain border-b border-border bg-card shadow-lg animate-in slide-in-from-top-2 fade-in duration-200">
            <div className="px-5 py-4 space-y-1">
              <Link href="/" onClick={() => setIsOpen(false)} className="flex min-h-11 items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-muted-bg">
                <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                </svg>
                Home
              </Link>

              <a
                href="https://x.com/KeeferGregoire"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Product updates on X (opens in a new tab)"
                onClick={() => setIsOpen(false)}
                className="ui-button ui-button--ghost w-full justify-start text-muted"
              >
                <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.64 7.584H.47l8.6-9.835L0 1.154h7.594l5.243 6.932 6.064-6.933Zm-1.29 19.49h2.039L6.486 3.24H4.298l13.313 17.403Z" />
                </svg>
                Updates
              </a>

              <Link href="/blog" onClick={() => setIsOpen(false)} className="flex min-h-11 items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-muted-bg">
                <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                </svg>
                Blog
              </Link>

              <Link href="/#pricing" onClick={() => setIsOpen(false)} className="flex min-h-11 items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-muted-bg">
                <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
                </svg>
                Pricing
              </Link>

              <ThemeToggle variant="menu" />

              {user && (
                <>
                  <LibraryNavigation onNavigate={() => setIsOpen(false)} />

                  <Link href="/account" onClick={() => setIsOpen(false)} className="flex min-h-11 items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-muted-bg">
                    <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
                    </svg>
                    Account
                  </Link>
                </>
              )}

              {/* Divider */}
              <div className="border-t border-border my-2" />

              {/* Usage + Auth */}
              {user ? (
                <>
                  <div className="px-3 py-2">
                    <UsageIndicator variant="menu" />
                  </div>
                  <div className="flex items-center gap-3 px-3 py-2.5">
                    <div className="h-8 w-8 shrink-0"><AccountAvatar image={identity.image} initials={identity.initials} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">
                        {identity.name}
                      </div>
                      <div className="text-xs text-muted truncate">{user.email}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => { setIsOpen(false); signOut(); }}
                    className="flex min-h-11 items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-muted-bg"
                  >
                    <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                    </svg>
                    Sign out
                  </button>
                </>
              ) : !loading ? (
                <button
                  onClick={() => { setIsOpen(false); openSignIn(); }}
                  className="ui-button ui-button--primary w-full"
                >
                  Sign in with Google
                </button>
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
